import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { apiFetch } from '../utils/apiClient';

const KnowledgeBaseManager = ({ embedded = false, onClose, isVisible = true }) => {
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [newKBData, setNewKBData] = useState({
    id: '',
    name: '',
    description: '',
    embedding_model: 'amazon.titan-embed-text-v2:0'
  });
  const [kbConfigs, setKbConfigs] = useState({});

  // Load KBs and sync data sources when component becomes visible
  useEffect(() => {
    if (isVisible) {
      const loadAndSync = async () => {
        await loadKnowledgeBases();
        // Auto-sync all KB data sources when tab opens
        await syncAllDataSources();
      };
      loadAndSync();
    }
  }, [isVisible]);



  const loadKnowledgeBases = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/api/knowledge-bases');
      if (!response.ok) throw new Error('Failed to load knowledge bases');
      const data = await response.json();
      setKnowledgeBases(data);
      
      // Initialize configs for each KB
      const configs = {};
      data.forEach(kb => {
        configs[kb.id] = {
          github_repos: kb.github_repos || ['DT-SFI/fni-forms-api', 'DT-SFI/fni-doc-center-api', 'DT-SFI/deal-signing-app', 'DT-SFI/test-integration-repo'],
          last_sync: kb.last_sync || null
        };
      });
      setKbConfigs(configs);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKB = async () => {
    if (!newKBData.id || !newKBData.name) {
      alert('Please provide KB ID and name');
      return;
    }

    setLoading(true);
    try {
      const response = await apiFetch('/api/knowledge-bases', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newKBData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to register knowledge base');
      }

      const result = await response.json();
      alert(`✓ ${result.message}`);
      setShowCreateForm(false);
      setNewKBData({ id: '', name: '', description: '', embedding_model: 'amazon.titan-embed-text-v2:0' });
      await loadKnowledgeBases();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Sync all KBs' data sources from AWS (silent mode for polling)
  const syncAllDataSources = async (silent = false) => {
    if (knowledgeBases.length === 0) return;
    
    try {
      await Promise.all(
        knowledgeBases.map(async (kb) => {
          try {
            const response = await apiFetch(`/api/knowledge-bases/${kb.id}/sync-data-sources`, {
              method: 'POST'
            });
            
            if (response.ok && !silent) {
              const result = await response.json();
              console.log(`Synced ${kb.name}:`, result.message);
            }
          } catch (err) {
            if (!silent) {
              console.error(`Failed to sync ${kb.name}:`, err.message);
            }
          }
        })
      );
      
      // Reload KBs after sync
      if (!silent) {
        await loadKnowledgeBases();
      }
    } catch (err) {
      if (!silent) {
        console.error('Error syncing data sources:', err);
      }
    }
  };

  const handleDeleteKB = async (kbId) => {
    if (!confirm(`Are you sure you want to unregister KB '${kbId}'? This will not delete the actual KB in AWS, only remove it from the registry.`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await apiFetch(`/api/knowledge-bases/${kbId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to unregister KB');
      }

      alert(`✓ Knowledge base '${kbId}' unregistered successfully`);
      await loadKnowledgeBases();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncKnowledgeBase = async (kbId) => {
    setSyncing(true);
    try {
      const config = kbConfigs[kbId];
      // Save configuration first
      const cleanedRepos = config.github_repos.filter(r => r.trim());
      const filePatterns = ['*']; // Fetch all files
      
      await apiFetch(`/api/knowledge-bases/${kbId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          github_repos: cleanedRepos,
          file_patterns: filePatterns
        })
      });
      
      // Then sync GitHub content
      const response = await apiFetch(`/api/knowledge-bases/${kbId}/sync-github`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          github_repos: cleanedRepos,
          file_patterns: filePatterns
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to sync GitHub content');
      }

      const result = await response.json();
      alert(`✓ ${result.message}\nFiles indexed: ${result.files_indexed || 0}`);
      
      // Update last sync time
      setKbConfigs(prev => ({
        ...prev,
        [kbId]: { ...prev[kbId], last_sync: new Date().toISOString() }
      }));
      
      await loadKnowledgeBases();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const updateKBConfig = (kbId, field, value) => {
    setKbConfigs(prev => ({
      ...prev,
      [kbId]: { ...prev[kbId], [field]: value }
    }));
  };

  const handleSaveKBConfig = async (kbId) => {
    setLoading(true);
    try {
      const config = kbConfigs[kbId];
      
      // Filter out empty lines before saving
      const cleanedRepos = config.github_repos.filter(r => r.trim());
      const filePatterns = ['*']; // Fetch all files
      
      // Update KB configuration via API
      const response = await apiFetch(`/api/knowledge-bases/${kbId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          github_repos: cleanedRepos,
          file_patterns: filePatterns
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save configuration');
      }

      alert(`✓ Configuration saved for KB '${kbId}'`);
      await loadKnowledgeBases();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <div className="overflow-y-auto max-h-full">
      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading knowledge bases...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          Error: {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Create KB Button */}
          <div className="mb-6">
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
            >
              <span>{showCreateForm ? '✕ Cancel' : '➕ Register Knowledge Base'}</span>
            </button>
          </div>

          {/* Create KB Form */}
          {showCreateForm && (
            <div className="mb-6 p-6 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Register Knowledge Base</h3>
              <p className="text-sm text-gray-600 mb-4">
                Register an existing AWS Bedrock Knowledge Base. The KB must already exist in your AWS account.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Knowledge Base ID* (from AWS)
                  </label>
                  <input
                    type="text"
                    value={newKBData.id}
                    onChange={(e) => setNewKBData(prev => ({ ...prev, id: e.target.value.toUpperCase() }))}
                    placeholder="e.g., HEWF0IGTXQ"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name*
                  </label>
                  <input
                    type="text"
                    value={newKBData.name}
                    onChange={(e) => setNewKBData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Product Documentation"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={newKBData.description}
                  onChange={(e) => setNewKBData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  placeholder="Describe what this knowledge base contains..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button
                onClick={handleCreateKB}
                disabled={loading || !newKBData.id || !newKBData.name}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Register Knowledge Base
              </button>
            </div>
          )}

          {/* Knowledge Bases List */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Registered Knowledge Bases</h3>
            {knowledgeBases.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                No knowledge bases registered. Register an existing AWS KB to get started!
              </p>
            ) : (
              knowledgeBases.map((kb) => (
                <div key={kb.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h4 className="text-lg font-medium text-gray-800">{kb.name}</h4>
                      <p className="text-sm text-gray-600 mt-1">{kb.description}</p>
                      <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                        <span className="font-mono bg-gray-100 px-2 py-1 rounded">{kb.id}</span>
                        <span>🗂️ {kb.data_sources?.length || 0} data sources</span>
                        <span>📊 {kb.embedding_model}</span>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleSaveKBConfig(kb.id)}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                        title="Save configuration"
                      >
                        💾 Save
                      </button>
                      <button
                        onClick={() => handleDeleteKB(kb.id)}
                        className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                      >
                        🗑️
                      </button>
                      <span className="text-xs text-gray-500 italic">
                        💡 After saving, use "Sync GitHub Content" below to apply changes
                      </span>
                    </div>
                  </div>
                  
                  {/* GitHub Integration Configuration Section */}
                  {kbConfigs[kb.id] && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-4">
                      <h5 className="font-medium text-gray-800 mb-3">📝 GitHub Integration Settings</h5>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          GitHub Repositories
                          <span className="text-gray-500 font-normal ml-1">(one per line)</span>
                        </label>
                        <textarea
                          value={kbConfigs[kb.id].github_repos.join('\n')}
                          onChange={(e) => updateKBConfig(kb.id, 'github_repos', e.target.value.split('\n'))}
                          className="w-full px-3 py-2 border border-gray-300 rounded font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          rows={4}
                          placeholder="DT-SFI/fni-forms-api&#10;DT-SFI/fni-doc-center-api"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Format: owner/repository
                        </p>
                      </div>

                      <div className="pt-2 flex items-center space-x-4">
                        <button
                          onClick={() => handleSyncKnowledgeBase(kb.id)}
                          disabled={syncing || kbConfigs[kb.id].github_repos.length === 0}
                          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {syncing ? (
                            <>
                              <span className="animate-spin">⟳</span>
                              <span>Syncing GitHub...</span>
                            </>
                          ) : (
                            <>
                              <span>🔄</span>
                              <span>Sync GitHub Content</span>
                            </>
                          )}
                        </button>
                        {kbConfigs[kb.id].github_repos.length === 0 && (
                          <p className="text-xs text-amber-600">
                            ⚠️ Add GitHub repositories to sync
                          </p>
                        )}
                      </div>

                      {kbConfigs[kb.id].last_sync && (
                        <div className="text-xs text-gray-600 pt-2 border-t border-gray-200">
                          Last GitHub sync: {new Date(kbConfigs[kb.id].last_sync).toLocaleString()}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Data Sources */}
                  {kb.data_sources && kb.data_sources.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Data Sources:</h5>
                      <div className="space-y-2">
                        {kb.data_sources.map((ds) => (
                          <div key={ds.id} className="text-sm bg-gray-50 p-2 rounded">
                            <div className="flex justify-between">
                              <span className="font-medium">{ds.name}</span>
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                ds.status === 'AVAILABLE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {ds.status}
                              </span>
                            </div>
                            <span className="text-gray-500 font-mono text-xs">{ds.id}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Knowledge Base Management</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ×
          </button>
        </div>
        <div className="p-6">
          {content}
        </div>
      </div>
    </div>
  );
};

KnowledgeBaseManager.propTypes = {
  embedded: PropTypes.bool,
  onClose: PropTypes.func
};

export default KnowledgeBaseManager;
